import {
  ChevronDown,
  FileText,
  Import,
  Settings,
  User,
  Users,
} from "lucide-react";
import { CanAccess, useTranslate, useUserMenu } from "ra-core";
import { Fragment } from "react";
import { Link, useLocation } from "react-router";

import { RefreshButton } from "@/components/admin/refresh-button";
import { ThemeModeToggle } from "@/components/admin/theme-mode-toggle";
import { UserMenu } from "@/components/admin/user-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

import { ChangelogPage } from "../misc/ChangelogPage";
import { ImportPage } from "../misc/ImportPage";
import { useInboxBadgeCount } from "../whatsapp/inbox/useInbox";
import { BrandMark } from "./BrandMark";
import {
  MORE_NAV_GROUPS,
  PRIMARY_NAV_ITEMS,
  isMoreNavActive,
  isNavItemActive,
  navLabel,
  type NavItem,
} from "./navigation";
import {
  useAccessibleNavGroups,
  useAccessibleNavItems,
} from "./useAccessibleNavItems";

const Header = () => {
  const translate = useTranslate();
  const { pathname } = useLocation();
  const primaryItems = useAccessibleNavItems(PRIMARY_NAV_ITEMS);

  return (
    <header className="bg-secondary">
      <div className="flex items-center gap-2 px-4">
        <BrandMark titleClassName="hidden lg:inline" />
        <nav
          aria-label={translate("crm.navigation.label")}
          className="min-w-0 flex-1 overflow-x-auto"
        >
          <div className="flex w-max min-w-full justify-center">
            {primaryItems.map((item) => (
              <NavigationTab
                key={item.id}
                label={navLabel(item, translate)}
                to={item.to}
                isActive={isNavItemActive(item, pathname)}
              />
            ))}
            <ChatTab />
            <MoreNavigationMenu isActive={isMoreNavActive(pathname)} />
          </div>
        </nav>
        <div className="flex shrink-0 items-center">
          <ThemeModeToggle />
          <RefreshButton />
          <UserMenu>
            <ProfileMenu />
            <CanAccess resource="sales" action="list">
              <UsersMenu />
            </CanAccess>
            <CanAccess resource="configuration" action="edit">
              <SettingsMenu />
            </CanAccess>
            <ImportFromJsonMenuItem />
            <ChangelogMenuItem />
          </UserMenu>
        </div>
      </div>
    </header>
  );
};

const tabClassName = (isActive: boolean) =>
  cn(
    "inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-2.5 py-3 text-sm font-medium transition-colors md:px-3 xl:px-4",
    isActive
      ? "border-secondary-foreground text-secondary-foreground"
      : "border-transparent text-secondary-foreground/70 hover:text-secondary-foreground/90",
  );

const NavigationTab = ({
  label,
  to,
  isActive,
}: {
  label: string;
  to: string;
  isActive: boolean;
}) => (
  <Link
    to={to}
    className={tabClassName(isActive)}
    aria-current={isActive ? "page" : undefined}
  >
    {label}
  </Link>
);

/**
 * The team WhatsApp inbox (`/chat`) is its own small app outside the CRM's hash
 * router, so this is a plain link (full page load into a ~190 KB bundle).
 */
const ChatTab = () => {
  const translate = useTranslate();
  const inboxCount = useInboxBadgeCount();
  return (
    <a href="/chat" className={tabClassName(false)}>
      {translate("crm.navigation.chat", { _: "Chat" })}
      <NavCountBadge count={inboxCount} />
    </a>
  );
};

/** Small count pill; hidden at zero so quiet inboxes stay quiet. */
export const NavCountBadge = ({
  count,
  className,
}: {
  count: number | null | undefined;
  className?: string;
}) => {
  if (count == null || count <= 0) return null;
  return (
    <span
      className={cn(
        "inline-flex min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[11px] font-semibold leading-5 text-white tabular-nums",
        className,
      )}
      aria-label={`${count} waiting`}
    >
      {count > 99 ? "99+" : count}
    </span>
  );
};

/**
 * Every destination that is not one of the six primary tabs, grouped by team.
 * The trigger lights up when the current page lives inside it and carries the
 * inbox needs-reply count so tucking Inbox away loses no signal.
 */
const MoreNavigationMenu = ({ isActive }: { isActive: boolean }) => {
  const translate = useTranslate();
  const groups = useAccessibleNavGroups(MORE_NAV_GROUPS);
  const inboxCount = useInboxBadgeCount();

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(tabClassName(isActive), "cursor-pointer")}
          aria-current={isActive ? "page" : undefined}
        >
          {translate("crm.navigation.more", { _: "More" })}
          <ChevronDown className="size-3.5 opacity-70" aria-hidden="true" />
          <NavCountBadge count={inboxCount} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" sideOffset={2} className="w-64">
        {groups.map((group, index) => (
          <Fragment key={group.id}>
            {index > 0 ? <DropdownMenuSeparator /> : null}
            <DropdownMenuLabel className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {group.label}
            </DropdownMenuLabel>
            {group.items.map((item) => (
              <MoreMenuItem
                key={item.id}
                item={item}
                badge={item.badge === "inbox" ? inboxCount : null}
              />
            ))}
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const MoreMenuItem = ({
  item,
  badge,
}: {
  item: NavItem;
  badge: number | null;
}) => {
  const translate = useTranslate();
  const Icon = item.icon;
  return (
    <DropdownMenuItem asChild>
      <Link to={item.to} className="flex items-center gap-2">
        <Icon className="size-4 text-muted-foreground" aria-hidden="true" />
        <span className="flex-1">{navLabel(item, translate)}</span>
        <NavCountBadge count={badge} />
      </Link>
    </DropdownMenuItem>
  );
};

const UsersMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<UsersMenu> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/sales" className="flex items-center gap-2">
        <Users />
        {translate("resources.sales.name", { smart_count: 2 })}
      </Link>
    </DropdownMenuItem>
  );
};

const ProfileMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ProfileMenu> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/profile" className="flex items-center gap-2">
        <User />
        {translate("crm.profile.title")}
      </Link>
    </DropdownMenuItem>
  );
};

const SettingsMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<SettingsMenu> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/settings" className="flex items-center gap-2">
        <Settings />
        {translate("crm.settings.title")}
      </Link>
    </DropdownMenuItem>
  );
};

const ImportFromJsonMenuItem = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ImportFromJsonMenuItem> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={ImportPage.path} className="flex items-center gap-2">
        <Import />
        {translate("crm.header.import_data")}
      </Link>
    </DropdownMenuItem>
  );
};

const ChangelogMenuItem = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ChangelogMenuItem> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={ChangelogPage.path} className="flex items-center gap-2">
        <FileText />
        {translate("crm.changelog.title")}
      </Link>
    </DropdownMenuItem>
  );
};

export default Header;
